import React, { useState } from "react";
import { KpiIcon } from "../../KpiIcons";
import { fmt } from "./utils";
import { formatNumber } from "../../../../../../utils/numberFormat";
import Av from "./Avatar";
import ActionBadge from "./ActionBadge";
import DocumentViewerPanel from "./DocumentViewerPanel";

const HistoryEntryCard = ({ entry, locs, isLast, index }) => {
  const [docOpen, setDocOpen] = useState(false);

  const locId = entry.locationId || entry.location_id;
  const locObj = locs.find((l) => String(l.id) === String(locId));
  const fromDate = entry.fromDate || entry.from_date;
  const toDate = entry.toDate || entry.to_date;
  const by = entry.changedBy;

  // Action colour for the left timeline dot
  const dotColor =
    entry.action === "CREATE" ? "var(--green)" : "var(--blue)";

  return (
    <div style={{ display: "flex", gap: 14 }}>
      {/* Timeline spine */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          width: 18,
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: dotColor,
            border: `2px solid ${
              entry.action === "CREATE" ? "#bbf7d0" : "var(--blue2)"
            }`,
            marginTop: 4,
            flexShrink: 0,
          }}
        />
        {!isLast && (
          <div
            style={{
              width: 2,
              flex: 1,
              background: "var(--bdr2)",
              marginTop: 4,
              minHeight: 24,
            }}
          />
        )}
      </div>

      {/* Card */}
      <div style={{ flex: 1, marginBottom: isLast ? 0 : 16 }}>
        {/* Card header row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <ActionBadge action={entry.action} />
            {by && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <Av user={by} size={20} />
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "var(--ink2)",
                  }}
                >
                  {by.first_name} {by.last_name}
                </span>
                {by.email && (
                  <span style={{ fontSize: 10, color: "var(--ink4)" }}>
                    · {by.email}
                  </span>
                )}
              </div>
            )}
          </div>
          <span
            style={{
              fontSize: 10.5,
              color: "var(--ink4)",
              fontFamily: "var(--fm)",
              flexShrink: 0,
            }}
          >
            {fmt(entry.createdAt || entry.created_at, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}{" "}
            <span style={{ color: "var(--ink4)", fontSize: 9.5 }}>
              {entry.createdAt
                ? new Date(entry.createdAt).toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </span>
          </span>
        </div>

        {/* Snapshot data grid */}
        <div
          style={{
            background: "var(--card)",
            border: "1px solid var(--bdr)",
            borderRadius: 9,
            padding: "10px 14px",
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          {/* Value */}
          <div>
            <div
              style={{
                fontSize: 8.5,
                fontWeight: 700,
                color: "var(--ink4)",
                textTransform: "uppercase",
                letterSpacing: ".07em",
                fontFamily: "var(--fm)",
                marginBottom: 2,
              }}
            >
              Value
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: "var(--ink)",
                fontFamily: "var(--fm)",
                lineHeight: 1,
              }}
            >
              {entry.value != null ? formatNumber(entry.value) : "—"}
              {entry.unit && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: "var(--ink4)",
                    marginLeft: 4,
                  }}
                >
                  {entry.unit}
                </span>
              )}
            </div>
          </div>

          {/* Date range */}
          {(fromDate || toDate) && (
            <div>
              <div
                style={{
                  fontSize: 8.5,
                  fontWeight: 700,
                  color: "var(--ink4)",
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                  fontFamily: "var(--fm)",
                  marginBottom: 2,
                }}
              >
                Date Range
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--ink2)",
                  lineHeight: 1.4,
                }}
              >
                {fromDate
                  ? fmt(fromDate, { day: "2-digit", month: "short" })
                  : "—"}
                <span style={{ color: "var(--ink4)", margin: "0 5px" }}>
                  →
                </span>
                {toDate
                  ? fmt(toDate, { day: "2-digit", month: "short" })
                  : "—"}
              </div>
            </div>
          )}

          {/* Location */}
          {locId && (
            <div>
              <div
                style={{
                  fontSize: 8.5,
                  fontWeight: 700,
                  color: "var(--ink4)",
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                  fontFamily: "var(--fm)",
                  marginBottom: 2,
                }}
              >
                Location
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--ink2)",
                  lineHeight: 1.4,
                }}
              >
                {locObj?.name || `ID: ${locId}`}
              </div>
            </div>
          )}

          {/* Field-level diff (if present) */}
          {entry.fieldName && (
            <div style={{ flex: 1, minWidth: 160 }}>
              <div
                style={{
                  fontSize: 8.5,
                  fontWeight: 700,
                  color: "var(--ink4)",
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                  fontFamily: "var(--fm)",
                  marginBottom: 2,
                }}
              >
                Field Changed
              </div>
              <div style={{ fontSize: 11.5, fontFamily: "var(--fm)" }}>
                <span style={{ color: "var(--ink3)", marginRight: 4 }}>
                  {entry.fieldName}:
                </span>
                <span
                  style={{
                    color: "var(--red)",
                    textDecoration: "line-through",
                  }}
                >
                  {entry.oldValue ?? "—"}
                </span>
                <span style={{ color: "var(--ink4)", margin: "0 5px" }}>
                  →
                </span>
                <span style={{ color: "var(--green)", fontWeight: 700 }}>
                  {entry.newValue ?? "—"}
                </span>
              </div>
            </div>
          )}

          {/* Document indicator */}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
            }}
          >
            {entry.documents?.length > 0 ? (
              <button
                onClick={() => setDocOpen((v) => !v)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 10px",
                  borderRadius: 7,
                  background: docOpen ? "var(--blue3)" : "transparent",
                  border: `1.5px solid ${
                    docOpen ? "var(--blue2)" : "var(--bdr)"
                  }`,
                  color: docOpen ? "var(--blue)" : "var(--ink3)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "var(--ff)",
                  transition: "all .15s",
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    minWidth: 12,
                    minHeight: 12,
                    maxWidth: 12,
                    maxHeight: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <KpiIcon
                    name="upload"
                    size={10}
                    color={docOpen ? "var(--blue)" : "var(--ink3)"}
                  />
                </div>
                {docOpen ? "Hide doc" : "View doc"}
              </button>
            ) : (
              <span
                style={{
                  fontSize: 10,
                  color: "var(--ink4)",
                  padding: "4px 8px",
                  background: "var(--bdr2)",
                  borderRadius: 6,
                  border: "1px solid var(--bdr)",
                }}
              >
                No doc
              </span>
            )}
          </div>
        </div>

        {/* Document viewer — inline below the card */}
        {docOpen && entry.documents?.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8, animation: "ahpDown .15s ease both" }}>
            {entry.documents.map((doc, i) => (
              <DocumentViewerPanel key={doc.id ?? i} document={doc} />
            ))}
          </div>
        )}
        {docOpen && !(entry.documents?.length > 0) && (
          <div
            style={{
              marginTop: 8,
              padding: "10px 14px",
              borderRadius: 9,
              background: "var(--bdr2)",
              border: "1px dashed var(--bdr)",
              fontSize: 11.5,
              color: "var(--ink4)",
              textAlign: "center",
            }}
          >
            Document metadata not yet loaded with this entry.
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryEntryCard;
